import type { Recipe } from '@/service/recipe/types';

export const RECIPE_SORTS = [
  'recent',
  'title',
  'rating',
  'preparationTime',
] as const;

export type RecipeSort = (typeof RECIPE_SORTS)[number];

export interface RecipeFilter {
  search: string;
  tags: string[];
  favoritesOnly: boolean;
  sort: RecipeSort;
}

export const DEFAULT_FILTER: RecipeFilter = {
  search: '',
  tags: [],
  favoritesOnly: false,
  sort: 'recent',
};

function normalise(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

// Average of everyone's stars; unrated recipes have none
export function averageRating(
  recipe: Pick<Recipe, 'rating'>,
): number | undefined {
  const ratings = Object.values(recipe.rating ?? {});
  return ratings.length
    ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
    : undefined;
}

// Every word of the search appears in the title, description or an
// ingredient
function matchesSearch(recipe: Recipe, search: string): boolean {
  const words = normalise(search).split(' ').filter(Boolean);
  const text = normalise(
    [
      recipe.title,
      recipe.subTitle,
      ...(recipe.ingredients ?? []).map(({ text }) => text),
    ].join(' '),
  );
  return words.every((word) => text.includes(word));
}

// Missing values (unrated, no time, saved before createdAt) sort last
function compareMissingLast(
  a: number | undefined,
  b: number | undefined,
): number {
  if (a === undefined || b === undefined) {
    return (a === undefined ? 1 : 0) - (b === undefined ? 1 : 0);
  }
  return a - b;
}

const sorts: Record<RecipeSort, (a: Recipe, b: Recipe) => number> = {
  title: () => 0,
  recent: (a, b) =>
    compareMissingLast(
      a.createdAt === undefined ? undefined : -a.createdAt,
      b.createdAt === undefined ? undefined : -b.createdAt,
    ),
  rating: (a, b) => {
    const ra = averageRating(a);
    const rb = averageRating(b);
    return compareMissingLast(
      ra === undefined ? undefined : -ra,
      rb === undefined ? undefined : -rb,
    );
  },
  preparationTime: (a, b) =>
    compareMissingLast(
      a.preparationTime || undefined,
      b.preparationTime || undefined,
    ),
};

// The recipes matching the filter, sorted; ties are broken by title
export function findRecipes(
  recipes: Recipe[],
  filter: RecipeFilter,
  favorites: string[],
): Recipe[] {
  return recipes
    .filter(
      (recipe) =>
        matchesSearch(recipe, filter.search) &&
        filter.tags.every((tag) => (recipe.tags ?? []).includes(tag)) &&
        (!filter.favoritesOnly || favorites.includes(recipe.id)),
    )
    .sort(
      (a, b) =>
        sorts[filter.sort](a, b) ||
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
    );
}

// Every tag used by the recipes, alphabetically, for suggestions and filters
export function allTags(recipes: Recipe[]): string[] {
  return [...new Set(recipes.flatMap((recipe) => recipe.tags ?? []))].sort(
    (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}
